import { ReactNode } from 'react';
import { create, StateCreator } from 'zustand';
import { CustomerId, MissionCustomer, Pilot, Site, SiteId, User } from '../types';
import { Acl } from '../api_services/types';
import { MissionType } from '../consts/missionType';
import { SelectedDateChangeMission, MissionData } from '../components/main_container/missions/types';
import { GcpRequest } from '../types/gcpRequet.types';
import { devtools } from 'zustand/middleware';
import { RescheduleReason } from '../consts/RescheduleReason';

// If an object allows for both undefined and null:
// undefined = not loaded yet
// null = loaded, but server returned null

export type SidebarPanel = 'FLIGHTS' | 'PROJECTS' | 'MISSIONS' | 'UPLOADS' | 'INVOICING' | 'DOCUMENTS';

type State = {
  user?: User;
  permissions?: Acl;
  pilot?: Pilot | null; // Refers to UAS Pilots AT record
  missions?: MissionCustomer[];
  missionFilter: MissionType | null;
  projectSearch: string | null;
  selectedMission: MissionData | null;
  selectedDateChangeMission: SelectedDateChangeMission | null;
  /**
   * Stores a deferred action that should be executed after confirming
   * navigation away from the mission window with unsent GCP uploads.
   * For example: navigating to another sidebar tab, logging out, or closing the window.
   * Set by `openGcpLeaveModal`, cleared by `closeGcpLeaveModal`.
   */
  pendingGcpLeaveAction: (() => void) | null;
  /**
   * Controls visibility of the GCP Leave Confirmation modal.
   * This modal warns the user that they have unsent GCP request files.
   */
  isGcpLeaveModalOpen: boolean;
  selectedSidebarPanel?: SidebarPanel;
  window?: ReactNode;
  cachedGcpRequests: {
    [key: string]: GcpRequest | null | undefined;
  };
  cachedFlightMarkersLastModified: {
    [key: string]: string | null | undefined;
  };
};

type Actions = {
  setUser: (user?: User) => void;
  setPermissions: (permissions?: Acl) => void;
  setPilot: (pilot?: Pilot | null) => void;
  setMissions: (missions: MissionCustomer[]) => void;
  setMissionFilter: (missionFilter: MissionType | null) => void;
  setProjectSearch: (projectSearch: string | null) => void;
  setSelectedSidebarPanel: (selectedSidebarPanel: SidebarPanel) => void;
  showWindow: (window: ReactNode) => void;
  hideWindow: () => void;
  getCustomer: (customerId: CustomerId) => MissionCustomer | undefined;
  getSite: (customerId: CustomerId, siteId: SiteId) => Site | undefined;
  getUser: () => User | undefined;
  setSelectedMission: (selectedMission: MissionData | null) => void;
  setSelectedDateChangeMission: (selectedDateChangeMission: SelectedDateChangeMission | null) => void;
  openGcpLeaveModal: (next: () => void) => void;
  closeGcpLeaveModal: () => void;
  getGcpRequest: (customerId: string, siteId: string) => GcpRequest | null | undefined;
  setGcpRequest: (customerId: string, siteId: string, gcpRequest: GcpRequest | null) => void;
  getFlightMarkersLastModified: (customerId: string, siteId: string) => string | null | undefined;
  setFlightMarkersLastModified: (customerId: string, siteId: string, lastModified: string | null) => void;
};

const initialState: State = {
  user: undefined,
  pilot: undefined,
  missions: undefined,
  missionFilter: null,
  projectSearch: null,
  selectedMission: null,
  selectedDateChangeMission: null,
  pendingGcpLeaveAction: null,
  isGcpLeaveModalOpen: false,
  selectedSidebarPanel: 'MISSIONS',
  window: undefined,
  cachedGcpRequests: {},
  cachedFlightMarkersLastModified: {},
};

const createAppStore: StateCreator<State & Actions, [['zustand/devtools', never]], []> = (set, get) => ({
  ...initialState,

  setUser: (user) => set(() => ({ user })),
  setPermissions: (permissions) => set(() => ({ permissions })),
  setPilot: (pilot) => set(() => ({ pilot })),

  setMissions: (missions) => set(() => ({ missions })),
  setMissionFilter: (missionFilter) => set(() => ({ missionFilter })),
  setProjectSearch: (projectSearch) => set(() => ({ projectSearch })),

  setSelectedSidebarPanel: (selectedSidebarPanel) => set(() => ({ selectedSidebarPanel })),

  setSelectedMission: (selectedMission) => set(() => ({ selectedMission })),

  setSelectedDateChangeMission: (selectedDateChangeMission) => set(() => ({ selectedDateChangeMission })),

  /**
   * Opens the GCP Leave Confirmation modal and stores the action to execute
   * if the user confirms they want to leave despite unsent GCP uploads.
   * @param nextAction - Function to run after user confirms leaving.
   */
  openGcpLeaveModal: (nextAction: () => void) => set({ pendingGcpLeaveAction: nextAction, isGcpLeaveModalOpen: true }),

  /**
   * Closes the GCP Leave Confirmation modal and clears the stored leave action.
   * Called when the user chooses to stay or after executing the deferred action.
   */
  closeGcpLeaveModal: () => set({ isGcpLeaveModalOpen: false, pendingGcpLeaveAction: null }),

  showWindow: (window) => set(() => ({ window })),
  hideWindow: () => set(() => ({ window: undefined })),

  getCustomer: (customerId: CustomerId) => get().missions?.find((m) => m.id === customerId),
  getSite: (customerId: CustomerId, siteId: SiteId) =>
    get()
      .missions?.find((m) => m.id === customerId)
      ?.sites.find((s) => s.id === siteId),
  getUser: () => get().user,

  getGcpRequest: (customerId: string, siteId: string) => get().cachedGcpRequests[customerId + siteId],
  setGcpRequest: (customerId: string, siteId: string, gcpRequest: GcpRequest | null) =>
    set((state) => ({
      cachedGcpRequests: {
        ...state.cachedGcpRequests,
        [customerId + siteId]: gcpRequest,
      },
    })),

  getFlightMarkersLastModified: (customerId: string, siteId: string) =>
    get().cachedFlightMarkersLastModified[customerId + siteId],
  setFlightMarkersLastModified: (customerId: string, siteId: string, lastModified: string | null) =>
    set((state) => ({
      cachedFlightMarkersLastModified: {
        ...state.cachedFlightMarkersLastModified,
        [customerId + siteId]: lastModified,
      },
    })),
});

export const useAppStore = create<State & Actions>()(
  devtools(createAppStore, {
    name: 'AppStore',
    enabled: !isProduction,
  }),
);
